import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import config from "./config";
import { Spinner } from "./PageLoader";
import { addOutsidePress, removeOutsidePress } from "./outsidePress";

/**
 * Location search + tiny preview map used inside the profile modal.
 * Debounced text input → `membership-geocode` (Nominatim proxy); selecting a
 * suggestion fills { location, country, state, city, latitude, longitude } via
 * onChange and drops a marker on a small read-only Leaflet map.
 */

// HTML pin marker (avoids Leaflet's default-marker image-path issue).
function pinIcon(primary) {
  return L.divIcon({
    className: "ff-pin",
    html: `<div style="width:22px;height:22px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${primary};box-shadow:0 1px 4px rgba(0,0,0,.4);border:2px solid rgb(var(--c-surface))"></div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 22],
  });
}

// Keep the preview map centered on the selected point (and fix sizing inside
// a modal that may have laid out at 0×0 before the map appeared).
function Recenter({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lng != null) {
      map.setView([lat, lng], 11, { animate: false });
    }
    const t = setTimeout(() => map.invalidateSize(), 60);
    return () => clearTimeout(t);
  }, [lat, lng, map]);
  return null;
}

export default function LocationPicker({ token, primary = "#0274F7", value, onChange, error }) {
  const [query, setQuery] = useState(value?.location || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const timer = useRef(null);
  const boxRef = useRef(null);

  // Reflect external changes (e.g. initial profile load) into the input.
  useEffect(() => {
    setQuery(value?.location || "");
  }, [value?.location]);

  // Close the suggestions dropdown when clicking outside.
  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    addOutsidePress(onDocClick);
    return () => removeOutsidePress(onDocClick);
  }, []);

  const runSearch = (q) => {
    clearTimeout(timer.current);
    if (q.trim().length < 3) {
      setResults([]);
      setOpen(false);
      return;
    }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.post(`${config.COMMUNITY_API_URL}/membership-geocode`, {
          community_token: token,
          q,
        });
        setResults(res.data?.results || []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 450);
  };

  const handleType = (v) => {
    setQuery(v);
    // Propagate the raw text with NO coordinates. Previously typing only moved
    // local state, so free text left the previously-picked place (and its
    // lat/lon) intact — the field showed "dfhdfhdhdh" while the record still
    // held Chennai, and saving silently kept the old location. Clearing the
    // coords here is also what lets the parent tell "typed but not chosen"
    // apart from "chosen from the list".
    onChange?.({
      location: v,
      country: "",
      state: "",
      city: "",
      latitude: null,
      longitude: null,
    });
    runSearch(v);
  };

  const pick = (r) => {
    onChange?.({
      location: r.label,
      country: r.country || "",
      state: r.state || "",
      city: r.city || "",
      latitude: r.lat,
      longitude: r.lon,
    });
    setQuery(r.label);
    setResults([]);
    setOpen(false);
  };

  const clear = () => {
    onChange?.({ location: "", country: "", state: "", city: "", latitude: null, longitude: null });
    setQuery("");
    setResults([]);
    setOpen(false);
  };

  const hasLoc = value?.latitude != null && value?.longitude != null;

  return (
    <div ref={boxRef} className="relative">
      <span className="text-[13px] font-medium text-gray-600 mb-1.5 flex items-center gap-1">
        Location
        <span className="text-gray-400 font-normal">(optional)</span>
      </span>

      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z" />
            <circle cx="12" cy="11" r="2.5" />
          </svg>
        </span>
        <input
          type="text"
          value={query}
          placeholder="Search your city or address…"
          onFocus={() => {
            setFocused(true);
            if (results.length) setOpen(true);
          }}
          onBlur={() => setFocused(false)}
          onChange={(e) => handleType(e.target.value)}
          aria-invalid={error ? "true" : undefined}
          className="w-full rounded-xl border pl-9 pr-9 py-2.5 text-sm text-gray-800 outline-none"
          style={{
            borderColor: error ? "#dc2626" : focused ? primary : "rgb(var(--c-gray-200))",
            boxShadow: error
              ? "0 0 0 3px rgba(220,38,38,0.12)"
              : focused
              ? `0 0 0 3px ${primary}22`
              : "none",
          }}
        />
        {loading ? (
          <Spinner size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
        ) : (query || hasLoc) ? (
          <button
            type="button"
            onClick={clear}
            aria-label="Clear location"
            className="absolute right-2.5 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            ×
          </button>
        ) : null}
      </div>

      {/* Suggestions */}
      {open && results.length > 0 && (
        <ul className="absolute z-[10001] mt-1 w-full bg-surface rounded-xl border border-gray-200 shadow-lg max-h-56 overflow-y-auto">
          {results.map((r, i) => (
            <li key={i}>
              <button
                type="button"
                onClick={() => pick(r)}
                className="w-full text-left px-3.5 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-start gap-2"
              >
                <svg className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.828 0l-4.243-4.243a8 8 0 1111.314 0z" />
                  <circle cx="12" cy="11" r="2.5" />
                </svg>
                <span className="leading-snug">{r.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Validation / guidance. The map below only renders once a suggestion has
          been chosen, so without this the member gets no feedback at all that
          their typing hasn't resolved to a real place. */}
      {error ? (
        <p className="mt-1.5 text-[12px] font-medium text-red-600 dark:text-red-300">{error}</p>
      ) : query.trim() && !hasLoc ? (
        <p className="mt-1.5 text-[12px] text-gray-400">
          Pick a place from the list to set your location.
        </p>
      ) : null}

      {/* Preview map. `isolate` is load-bearing, not cosmetic: .leaflet-container
          creates no stacking context of its own, so its panes (z-index 400-700)
          otherwise compete with everything else in the modal and paint over any
          dropdown opened above the map — the country list and these suggestions
          both landed under the tiles. isolation:isolate keeps those panes
          contained so the map can only ever stack against its own siblings. */}
      {hasLoc && (
        <div className="isolate mt-2.5 rounded-xl overflow-hidden border border-gray-200">
          <MapContainer
            center={[value.latitude, value.longitude]}
            zoom={11}
            scrollWheelZoom={false}
            dragging={false}
            doubleClickZoom={false}
            zoomControl={false}
            attributionControl={false}
            style={{ height: 120, width: "100%" }}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Marker position={[value.latitude, value.longitude]} icon={pinIcon(primary)} />
            <Recenter lat={value.latitude} lng={value.longitude} />
          </MapContainer>
        </div>
      )}
    </div>
  );
}
